import type { ParsedQuery } from 'query-string'
import { useRef, useState } from 'react'

import { FvButton, LoadMore } from '@fv/client-components'
import { flattenPages } from '@fv/client-core'
import type { ShipmentListPreference } from '@fv/models'

import { ActiveShipmentsExportControls } from '@/components/ActiveShipmentsExportControls'
import LoadDetailPanel from '@/components/list/LoadDetailPanel'
import { ScheduledReportsControls } from '@/components/ScheduledReportsControls'
import { ShipperTooltip } from '@/components/ShipperTooltip'

import { HideForVendor } from '../auth/HasRole'
import { CompactFilterBar } from '../shipment-filters/CompactFilterBar'
import { ShipmentFilterPanel } from '../shipment-filters/FilterPanel'
import { useHandleFilterChange } from '../shipment-filters/hooks'
import { BulkShipmentActions } from './BulkShipmentActions'
import {
  isDefaultFilter,
  useActiveShipmentSelection,
  useConfirmedShipments,
} from './hooks/useConfirmedShipments'
import {
  useConfirmedListParams,
  useSetActiveLoadId,
} from './hooks/useShipmentListParams'
import { CONFIRMED_LOCAL_STORAGE_SORTDIR_KEY } from './hooks/useSortDirectionPreference'
import { ShipmentCardList } from './ShipmentCardList'
import { ShipmentSortSelect } from './ShipmentSortSelect'
import { ShipmentViewSelect } from './ShipmentViewSelect'
import { SortDirectionToggle } from './SortDirectionToggle'

type Props = {
  onViewChange: (view: ShipmentListPreference) => void
}

export const ConfirmedShipmentList = ({ onViewChange }: Props) => {
  const listRef = useRef<HTMLDivElement>(null)
  const shipmentsQuery = useConfirmedShipments()
  const shipments = flattenPages(shipmentsQuery.data?.pages)
  const { activeLoadId, qs, sortBy, sortDirection } =
    useConfirmedListParams(shipments)
  const setActiveLoadId = useSetActiveLoadId()
  const { handle: handleChange } = useHandleFilterChange(listRef)
  const [isScheduleOpen, setScheduleOpen] = useState(false)
  const { selected, setSelected, handleCheckAll, showBulkActions } =
    useActiveShipmentSelection(shipments)

  const handleFilterChange = (data: ParsedQuery) => {
    setSelected([])
    handleChange({ ...data, sortBy, sortDirection: sortDirection.toString() })
  }

  return (
    <div className="grid shipper-page-fill shipper-grid-rows-single-fill bg-[#f2f2f2] overflow-hidden grid-cols-[19rem_1fr_30.5rem]">
      <ShipmentFilterPanel
        className="b1400:col-start-1 b1400:row-start-1"
        drawerAt="b1400"
        onChange={handleFilterChange}
        onScheduleOpen={() => setScheduleOpen(true)}
      />

      <div
        className="shipment-list-ready px-6 py-8 bg-fv-gray-100 overflow-auto pt-0 b1400:relative b1400:col-start-1 b1400:col-span-2 b1400:row-start-1"
        id="shipmentList"
        ref={listRef}
      >
        <nav className="mb-6 fa-width-auto flex items-center gap-x-2 flex-wrap sticky top-0 z-[90]  border-b border-fv-gray bg-gray-50 -ml-6 -mr-[calc(1.5rem-2px)] px-6 pt-6 pb-4">
          <HideForVendor>
            <ShipperTooltip
              label={
                selected.length
                  ? 'Uncheck all cards'
                  : 'Check all cards in list'
              }
            >
              <FvButton
                icon={selected.length ? 'dash' : 'check-double'}
                theme="round"
                transform="shrink-2"
                className="ml-4 mr-2 !p-3"
                onClick={handleCheckAll}
              ></FvButton>
            </ShipperTooltip>
          </HideForVendor>
          <ShipmentViewSelect value="cards" onChange={onViewChange} />

          <div className="flex items-center gap-1">
            <SortDirectionToggle
              localStorageKey={CONFIRMED_LOCAL_STORAGE_SORTDIR_KEY}
              sortDirection={sortDirection}
            />
            <span className="b1450:hidden b1400:inline">by</span>
            <ShipmentSortSelect className="mr-1" value={sortBy} includeBooked />
          </div>

          <ActiveShipmentsExportControls
            queryString={isDefaultFilter(qs) ? `isArchived=false&${qs}` : qs}
          />

          <div className="b1400:block border-t-fv-gray mt-4 hidden basis-full border-l-0 border-t border-dashed pl-0 pt-4">
            <CompactFilterBar onChange={handleFilterChange} />
          </div>
        </nav>

        <ShipmentCardList
          query={shipmentsQuery}
          onActivate={l => setActiveLoadId(l.loadId)}
          selected={selected}
          setSelected={setSelected}
        />
        <LoadMore
          className="mt-4"
          fetchNextPage={shipmentsQuery.fetchNextPage}
          hasNextPage={shipmentsQuery.hasNextPage}
          isLoading={shipmentsQuery.isFetchingNextPage}
        />
      </div>

      <LoadDetailPanel key={activeLoadId} loadId={activeLoadId} />

      {showBulkActions && (
        <BulkShipmentActions
          selected={selected}
          onDone={() => setSelected([])}
        />
      )}

      <ScheduledReportsControls
        isOpen={isScheduleOpen}
        onClose={() => setScheduleOpen(false)}
      />
    </div>
  )
}
